/**
 * 客户邮件面板组件 — 在客户详情中展示该客户的往来邮件。
 *
 * 主要导出：`CrmCustomerEmailPanel`（自定义元素 `<crm-customer-email-panel>`）
 *
 * 功能特性：
 * - 列表/详情双模式切换
 * - 列表模式：使用 `<email-list-panel>` 按客户 ID 筛选邮件
 * - 详情模式：使用 `<email-viewer>` 展示邮件详情，带返回按钮
 * - 点击邮件后自动切换到详情模式并加载邮件完整数据
 *
 * 依赖关系：
 * - 继承 `BaseElement`
 * - 通过 `emailApi.get` 获取邮件详情
 * - 使用 `isSentByMe` 判断邮件是否由当前用户发送
 * - 内部使用 `<email-list-panel>` 和 `<email-viewer>` 子组件
 */
import { css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { Email } from '../types.js';
import { emailApi } from '../api.js';
import { BaseElement } from '../base-element.js';
import { isSentByMe } from '../utils/email-helpers.js';
import './email-list-panel.js';
import './email-viewer.js';

@customElement('crm-customer-email-panel')
export class CrmCustomerEmailPanel extends BaseElement {
  /** 当前客户 ID（用于筛选邮件列表） */
  @property({ type: Number }) customerId = 0;
  /** 当前客户名称（显示在标题栏） */
  @property({ type: String }) customerName = '';

  /** 当前查看的邮件对象（详情模式下使用） */
  @state() private viewingEmail: Email | null = null;
  /** 当前显示模式：'list' 邮件列表 / 'detail' 邮件详情 */
  @state() private mode: 'list' | 'detail' = 'list';

  static styles = css`
    :host { display: block; height: 100%; }
  `;

  /**
   * 外部调用方法 — 设置当前客户并重置到列表模式。
   * @param customerId - 客户 ID
   * @param customerName - 客户名称
   * @sideeffect 重置 mode 为 list，清空 viewingEmail，更新 customerId/customerName
   */
  show(customerId: number, customerName: string) {
    this.mode = 'list';
    this.viewingEmail = null;
    this.customerId = customerId;
    this.customerName = customerName;
  }

  /**
   * 邮件选择处理 — 切换到详情模式并加载邮件完整数据。
   * @param e - `crm-email-select` 事件，detail 包含 emailId
   * @sideeffect 调用 API 获取邮件详情，更新 viewingEmail 和 mode
   */
  private async handleEmailSelect(e: CustomEvent) {
    const emailId = e.detail.emailId;
    this.mode = 'detail';
    try {
      this.viewingEmail = await emailApi.get(emailId);
    } catch {
      // 获取失败时回退到列表模式
      this.mode = 'list';
    }
  }

  /**
   * 返回列表模式 — 清空当前查看的邮件。
   */
  private backToList() {
    this.mode = 'list';
    this.viewingEmail = null;
  }

  /**
   * 渲染客户邮件面板。
   * 布局：标题栏（返回按钮 + 客户名标题）→ 列表/详情内容区。
   * 详情模式下标题栏左侧显示返回箭头。
   * @returns Lit 模板
   */
  render() {
    return html`
      <div class="flex flex-col h-full">
        <div class="flex items-center px-4 py-3 border-b border-border flex-shrink-0">
          ${this.mode === 'detail'
            ? html`<button class="text-lg text-primary hover:text-primary-hover cursor-pointer mr-2 leading-none"
                          @click=${this.backToList}>←</button>`
            : ''}
          <h3 class="text-sm font-semibold text-text">${this.customerName} - 往来邮件</h3>
        </div>
        <div class="flex-1 min-h-0">
          ${this.mode === 'list'
            ? html`<email-list-panel
                    .customerId=${this.customerId}
                    .currentFolder=${''}
                    @crm-email-select=${this.handleEmailSelect}>
                  </email-list-panel>`
            : html`<email-viewer
                    .email=${this.viewingEmail}
                    .isSentByMe=${isSentByMe(this.viewingEmail)}
                  ></email-viewer>`}
        </div>
      </div>
    `;
  }
}
